import {
  useEffect,
  useState,
  useMemo,
} from 'react'
import {
  Navigate,
  useLocation,
} from 'react-router-dom'
import axios from "axios"
import qs from 'qs'
import Swal from 'sweetalert2'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Joyride, {
  ACTIONS,
  EVENTS,
  STATUS,
} from 'react-joyride'

import {
  hidePreloader,
  showPreloader,
  ROUTE_PATHS,
} from '../../../library/helper'
import AttachmentInstructionModal from "../../../components/AttachmentInstructionModal";
import Tour from "../../../components/OnboardingTour/Tour";
import {Button} from "@mui/material";
import {HelpOutline} from "@mui/icons-material";

const menuName = 'scope3'

export default function EmissionScope3Category11CreateInBulk() {
  const { t, i18n } = useTranslation()
  const account = useSelector((state) => state.account.value)
  const isParent = account?.is_parent === 1 ? true : false;
  const location = useLocation()

  const [redirectEmission, setRedirectEmission] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState(0)
  const [selectedOffice, setSelectedOffice] = useState(0)
  const [listCompany, setListCompany] = useState([])
  const [listOffice, setListOffice] = useState([])
  const [selectedFile, setSelectedFile] = useState()
  const [isSaving, setIsSaving] = useState(false)

  const [tourSteps, setTourSteps] = useState([])
  const [tourRun, setTourRun] = useState(false)
  const [tourStepIndex, setTourStepIndex] = useState(0)
  const [openInstructionModal, setOpenInstructionModal] = useState(false);

  useEffect(() => {
    if (!Array.isArray(account?.tours) || !account.tours.includes(menuName)) {
      setTourRun(true);
    }
  }, [account?.tours]);

  const tourInitialSteps = useMemo(
    () => [
      {
        content: t('emission.tour.company2'),
        spotlightClicks: true,
        disableBeacon: true,
        target: '#tour__company',
        title: t('emission.tour.company1'),
      },
      {
        spotlightClicks: true,
        target: '#tour__location',
        title: t('emission.tour.location'),
      },
      {
        spotlightClicks: true,
        target: '#tour__template',
        title: t('emission.tour.template'),
      },
      {
        spotlightClicks: true,
        target: '#tour__attachment',
        title: t('emission.tour.attachment.button'),
      },
      {
        target: '#tour__save',
        title: t('emission.tour.save.bulk'),
      },
      {
        target: '#tour__instruction',
        title: t('emission.tour.instruction'),
        content: t('emission.tour.instruction.content'),
      },
    ], [i18n.language])

  useEffect(() => {
    // Wrap inside the if condition below to prevent
    // invalid step warning from Joyride
    if (
      Array.isArray(tourInitialSteps) &&
      tourInitialSteps.length > 0
    ) {
      setTourSteps(tourInitialSteps)
    }
  }, [tourInitialSteps])

  useEffect(() => {
    (async () => {
      showPreloader()

      const optionsGetCompany = {
        withCredentials: true,
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        url: `${process.env.REACT_APP_BASE_SERVER}/client/company`
      };
      const responseGetCompany = await axios(optionsGetCompany)
      if (responseGetCompany.data.success) {
        setListCompany(responseGetCompany.data.data)
      }

      hidePreloader()
    })()
  }, [i18n.language])

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data
      console.log(action, index, status, type)

    if (([STATUS.FINISHED, STATUS.SKIPPED].includes(status))) {
      // Need to set our running state to false,
      // so we can restart if we click start again.
      setTourRun(false)
      setTourStepIndex(0)
    } else if (type === EVENTS.TARGET_NOT_FOUND) {
      setTourStepIndex(index - 1)
    } else if (type === EVENTS.STEP_AFTER) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setTourStepIndex(nextStepIndex)
    }
  }

  const startGuideline = () => {
    setTourRun(true)
  }

  const onSelectCompany = async (e) => {
    const companyValue = e.target.value
    const data = { 'coid': companyValue }
    const optionsGetOffice = {
      withCredentials: true,
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      data: qs.stringify(data),
      url: `${process.env.REACT_APP_BASE_SERVER}/client/office`
    }
    const responseGetOffice = await axios(optionsGetOffice)
    if (responseGetOffice.data.success) {
      setSelectedCompany(companyValue)
      setListOffice(responseGetOffice.data.data)
      setSelectedOffice(0)
    }
  }

  const onSelectOffice = (e) => {
    setSelectedOffice(e.target.value)
  }

  const onSaveRecord = async (e) => {
    e.preventDefault()
    if (isSaving) return
    setIsSaving(true)
    try {
      const data = new FormData();
      data.append('scope', 3)
      data.append('companyId', selectedCompany)
      data.append('locationId', selectedOffice)
      data.append('attachment', selectedFile)

      if (location.pathname === ROUTE_PATHS.scope3category4Create) {
        data.append('scope3CategoryId', 4)
      } else if (location.pathname === ROUTE_PATHS.scope3category9Create) {
        data.append('scope3CategoryId', 9)
      }

      const options = {
        withCredentials: true,
        method: 'POST',
        headers: {'content-type': 'multipart/form-data'},
        data: data,
        url: `${process.env.REACT_APP_BASE_SERVER}/use-of-sold-product-emission-record-bulk-submission`
      }
      const response = await axios(options)

      if (response.data.ok) {
        const message1 = t('scope3.cat6.submissionSuccessMessage.part1')
        const message2 = t('scope3.cat6.submissionSuccessMessage.part2')
        const message3 = t('scope3.cat6.submissionSuccessMessage.part3')
        Swal.fire({
          title: '',
          html: `<p>${message1} ${response.data?.data?.affectedRows} ${message2} ${response.data?.data?.attachmentAffectedRows} ${message3}</p>`,
          showCancelButton: false,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Ok'
        }).then((result) => {
          if (result.isConfirmed) {
            setRedirectEmission(true)
          }
          setIsSaving(false)
        })
      } else {
        setIsSaving(false)
      }
    } catch (error) {
      let message
      if (typeof error.response?.data?.message === 'string') {
        message = error.response.data.message
      } else if (error.message) {
        message = error.message
      }
      Swal.fire({
        title: '',
        icon: 'error',
        html: `<p>${message || 'Please fill all information needs'}</p>`,
        showCancelButton: false,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Ok',
      }).finally(() => setIsSaving(false))
    }
  }

  const onAttachFile = () => {
    document.getElementById('formFile').click()
  }

  const onChangeFile = (e) => {
    const files = e.target.files
    setSelectedFile(files?.[0])
  }

  const onDownloadTemplate = async () => {
    try {
      const scope3CategoryId = 11

      const baseUrl = process.env.PUBLIC_URL || ''
      const fileName = `Demo TruCount Scope 3 Category ${scope3CategoryId}.xlsx`
      const encodedFileName = encodeURIComponent(fileName).replace(/%20/g, ' ')

      // Try to fetch from scope-3 folder
      const exampleUrl = `${baseUrl}/assets/scope-3/${encodedFileName}`
      const resp = await fetch(exampleUrl)

      if (!resp.ok) {
        throw new Error(`Template file not found: ${fileName}`)
      }

      const blob = await resp.blob()

      if (blob.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        const text = await blob.text()
        throw new Error(`File yang diterima bukan file Excel.<br>Tipe: ${blob.type}<br>Isi awal: ${text.slice(0, 200)}`)
      }

      const anchor = document.createElement('a')
      const url = window.URL.createObjectURL(blob)
      anchor.href = url
      anchor.download = `Category ${scope3CategoryId} Scope 3 TruCount Template_${account.client_name}.xlsx`
      anchor.click()
      window.URL.revokeObjectURL(url)




    } catch (e) {
      let message
      if (typeof e.response?.data?.message === 'string') {
        message = e.response.data.message
      } else if (e.message) {
        message = e.message
      }
      Swal.fire({
        icon: 'error',
        title: '',
        html: `<p style="font-size:0.75rem;">${message}</p>`,
        showCancelButton: false,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Ok'
      })
    }
  }

  if (isParent) {
    return <Navigate to={ROUTE_PATHS.scope3category11} />
  }

  if (redirectEmission) {
    return <Navigate to={ROUTE_PATHS.scope3category11} />
  } else {
    const optionCompany = [
      ...selectedCompany <= 0
        ? [<option key={0} value={0}>
          {t('emission.form.placeholder1')}
        </option>]
        : [],
      ...listCompany.map((item) => {
        return <option key={item.company_id} value={item.company_id}>
          {item.company_name}
        </option>
      }),
    ]

    const optionOffice = [
      ...selectedOffice <= 0
        ? [<option key={0} value={0}>
          {t('emission.form.placeholder2')}
        </option>]
        : [],
      ...listOffice.map((item) => {
        return <option key={item.office_id} value={item.office_id}>
          {`${item.office_name}${item.office_address ? ` (${item.office_address})` : ''}`}
        </option>
      }),
    ]

    const closeTour = () => {
      setTourRun(false)
      setTourStepIndex(0)
    }

    return (
      <div className="page-content">
        <Tour run={tourRun}
              onEnd={closeTour}
              steps={tourSteps}
              menu={menuName}
              callback={handleJoyrideCallback}
              stepIndex={tourStepIndex}
              scrollToFirstStep
        />
        <div className="container-fluid">

          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className='mb-sm-0'>
                    {t('menu.scope3Category11Title')}
                  </h4>
                  <Button variant="contained" color="primary"
                          onClick={() => setTourRun(true)}
                          aria-label="Start Scope 3 Tour"
                  >
                    {t('dashboard.startTour')}
                  </Button>
              </div>
            </div>
          </div>


          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-header align-items-center d-flex">
                  <h4 className="card-title mb-0 flex-grow-1">
                    {t('general.btnCreateInBulk')}
                  </h4>
                </div>

                <div className="card-body">
                  <h5>{t('emission.form.info1')}</h5>
                  <p className='mb-3'>{t('emission.form.subInfo1')}</p>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3" id="tour__company">
                        <label htmlFor="company" className="form-label">
                          {t('emission.form.label1')}
                        </label>
                        <select
                          onChange={onSelectCompany}
                          value={selectedCompany}
                          id="company"
                          name="company"
                          className="form-select mb-3"
                        >
                          {optionCompany}
                        </select>
                      </div>
                    </div>
                    {selectedCompany > 0 ? <div className="col-md-6">
                      <div className="mb-3" id="tour__location">
                        <label htmlFor="office" className="form-label">
                          {t('emission.table.label2')}
                        </label>
                        <select
                          onChange={onSelectOffice}
                          value={selectedOffice}
                          id="company"
                          name="company"
                          className="form-select mb-3"
                        >
                          {optionOffice}
                        </select>
                      </div>
                    </div> : ""}
                  </div>

                  <div className="row">
                    <h5 className="pt-2">{t('emission.form.info2')}</h5>
                    <p className="mb-3">{t('emission.form.subInfo2')}</p>

                    <div className="col-md-12">
                      <div className="row">
                        <div className="col-md-12">
                          <button
                            id="tour__template"
                            className='btn btn-success'
                            onClick={onDownloadTemplate}
                          >
                            {t('general.btnDownloadTemplate')}
                          </button>
                          <div className="mb-3 mt-3">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <h5 htmlFor="tour__instruction" className="form-label mb-0">
                                {t('emission.form.label15')}
                              </h5>
                              <button
                                  type="button"
                                  onClick={() => setOpenInstructionModal(true)}
                                  className="btn btn-primary btn-sm d-flex align-items-center justify-content-center p-1"
                                  id="tour__instruction"
                              >
                                <HelpOutline fontSize="small" />
                              </button>
                              <AttachmentInstructionModal
                                  open={openInstructionModal}
                                  onClose={() => setOpenInstructionModal(false)}
                              />
                            </div>

                            <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  gap: '0.5rem',
                                  marginTop: '0.5rem',
                                }}
                            >
                              <button
                                  onClick={onAttachFile}
                                  type="button"
                                  className="btn btn-primary waves-effect waves-light"
                                  id="tour__attachment"
                              >
                                {t('emission.form.label17')}
                              </button>
                              <p className="text-muted mb-0">{selectedFile?.name}</p>
                            </div>

                            <input
                                onChange={onChangeFile}
                                style={{ display: 'none' }}
                                className="form-control"
                                type="file"
                                id="formFile"
                                accept=".zip"
                            />
                          </div>
                          <div className="col-md-6">
                            <p>{t('emission.form.label18')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="text-end">
                        <button
                            onClick={onSaveRecord}
                            type="submit"
                            className="btn btn-primary"
                            id="tour__save"
                            disabled={isSaving}
                            aria-busy={isSaving}
                            aria-disabled={isSaving}
                        >
                          {isSaving ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('general.btnSaving')}
                              </>
                          ) : (
                              t('general.btnSave')
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
